import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Chips } from '~/eds/src/components/Chips/Chips';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Table } from '~/eds/src/modules/Table/Table';
import { QueryFilters } from '~/features/advanced-search/query-filters/QueryFilters';
import { SearchQuery } from '~/features/advanced-search/types';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';

import { useGetLatestLibraryModelVersions } from '../hooks';
import { ModelPreview } from '../ModelLibrary/ModelPreview';
import { getEntityTypeIconProps } from '../ModelLibrary/utils';
import { LibraryModelVersion } from '../types';
import { useLibraryModelDetailsModal } from './useLibraryModelDetailsModal';

export const XRayLibraryAdmin = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const intl = useIntl();

  const { models, isLoading } = useGetLatestLibraryModelVersions({});

  const [saveLibraryModel, { isLoading: isSaving }] =
    api.endpoints.saveLibraryModel.useMutation();

  const libraryModel = useSelector(
    slices.xRayLibrary.selectors.selectAdminUpdateModel,
  );

  const [deleteLibraryModel, { isLoading: isDeleting }] =
    api.endpoints.deleteLibraryModel.useMutation();

  const columns = [
    {
      cellType: 'text',
      disableSortBy: true,
      key: 'name',
      title: intl.formatMessage({
        id: 'ModelLibraryAdmin.modelColumnTitle',
        defaultMessage: 'Model',
        description: 'Column title for model name',
      }),
      mapCellProps: (model: LibraryModelVersion) => ({
        text: model.label,
      }),
    },
    {
      disableSortBy: true,
      key: 'fieldType',
      title: intl.formatMessage({
        id: 'ModelLibraryAdmin.modelFieldTypeTitle',
        defaultMessage: 'Field Type',
        description: 'Column title for model field type',
      }),
      width: 's',
      renderCell: (model: LibraryModelVersion) => {
        const icon = getEntityTypeIconProps(model, intl);
        return <Icon {...icon} />;
      },
    },
    {
      key: 'scope',
      cellType: 'text',
      disableSortBy: true,
      title: intl.formatMessage({
        id: 'ModelLibraryAdmin.scopeColumnTitle',
        defaultMessage: 'Scope',
        description: 'Column title for model scope',
      }),
      width: 'l',
      renderCell: (model: LibraryModelVersion) => (
        <QueryFilters
          mode="preview"
          readOnly
          query={
            (model.config.scope?.data.attributes.query as SearchQuery) ?? []
          }
          onQueryChange={() => {}}
        />
      ),
    },
    {
      cellType: 'text',
      disableSortBy: true,
      key: 'version',
      title: intl.formatMessage({
        id: 'ModelLibraryAdmin.latestVersionColumnTitle',
        defaultMessage: 'Latest Version',
        description: 'Column title for latest version',
      }),
      width: 's',
      mapCellProps: (model: LibraryModelVersion) => ({
        text: model.version,
      }),
    },
    {
      cellType: 'text',
      disableSortBy: true,
      key: 'categories',
      title: intl.formatMessage({
        id: 'ModelLibraryAdmin.categoriesColumnTitle',
        defaultMessage: 'Categories',
        description: 'Column title for model categories',
      }),
      renderCell: (model: LibraryModelVersion) => {
        const chips = model.tags.map((tag) => ({
          text: tag.label,
        }));
        return <Chips chips={chips} />;
      },
    },
  ];

  const [libraryModelDetailsModal, showLibraryModelDetailsModal] =
    useLibraryModelDetailsModal({
      config: libraryModel?.config ?? null,
      isSaving,
      libraryModel,
      saveLibraryModel,
    });

  return (
    <>
      <Table
        columns={columns}
        data={models.map((model) => ({
          ...model,
          id: model.libraryModelId,
        }))}
        isLoading={isLoading}
        options={{
          enableSelectRows: false,
        }}
        rowActions={[
          {
            label: intl.formatMessage({
              id: 'ModelLibraryAdmin.updateActionLabel',
              defaultMessage: 'Update',
              description: 'Row action label for updating a model',
            }),
            isLoading: isSaving || isDeleting,
            onClick: (model: LibraryModelVersion) => {
              dispatch(slices.xRayLibrary.actions.setAdminUpdateModel(model));
              showLibraryModelDetailsModal();
            },
          },
          {
            label: intl.formatMessage({
              id: 'ModelLibraryAdmin.deleteActionLabel',
              defaultMessage: 'Delete',
              description: 'Row action label for deleting a model',
            }),
            isLoading: isSaving || isDeleting,
            onClick: (model: LibraryModelVersion) => {
              deleteLibraryModel({ libraryModelId: model.libraryModelId })
                .unwrap()
                .then(() => {
                  toast({
                    message: intl.formatMessage(
                      {
                        id: 'ModelLibraryAdmin.deleteSuccessMessage',
                        defaultMessage:
                          'Library Model {modelLabel} deleted successfully.',
                        description:
                          'Success message when a model is deleted. Example: modelLabel = "Important"',
                      },
                      {
                        modelLabel: model.label,
                      },
                    ),
                    status: 'success',
                  });
                });
            },
          },
        ]}
        rowDetails={{
          onClick: (model: LibraryModelVersion) =>
            dispatch(slices.xRayLibrary.actions.setModelPreview(model)),
        }}
        totalCount={models.length}
      />
      <ModelPreview hideActions />
      {libraryModelDetailsModal}
    </>
  );
};
